import {FC, useState, useCallback, useEffect, useRef} from "react";
import {makeStyles} from "tss-react/mui";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Box,
	Button,
	Chip,
	CircularProgress,
	FormControl,
	FormControlLabel,
	FormHelperText,
	Radio,
	RadioGroup,
	Stack,
	TextField,
	Typography
} from "@mui/material";
import {Link, useNavigate, useParams, useBlocker} from "react-router-dom";
import {useQueryClient} from "@tanstack/react-query";
import PlayCircleFilledWhiteOutlinedIcon from "@mui/icons-material/PlayCircleFilledWhiteOutlined";
import UploadFileOutlinedIcon from "@mui/icons-material/UploadFileOutlined";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	useGetAnnouncement,
	useCreateAnnouncement,
	useUpdateAnnouncement,
	usePublishAnnouncement,
	useDraftAnnouncement,
	ANNOUNCEMENTS_QUERY_KEYS,
	useBrightcoveQueryVideo,
	isBrightcoveQueryPlayable,
	useUploadLessonFile,
	type CreateAnnouncementBody,
	type UpdateAnnouncementBody
} from "@/app2/shared/data";
import {SpecificAudienceSelector} from "./SpecificAudienceSelector";
import {getAnnouncementAdminDisplayStatus} from "./announcementAdminStatus";
import {AdminHeader} from "@/pages/ManagerView/ContentCreation/AdminHeader";
import {AdminInputText} from "@/pages/ManagerView/ContentCreation/AdminInputText";
import {AttachLinks} from "@/pages/ManagerView/ContentCreation/AttachLinks";
import {
	UploadBrightcoveVideo,
	completeBrightcoveIngest
} from "@/app2/components/UploadBrightcoveVideo";
import {useImproveAxios} from "@/app2/shared/hooks/useImproveAxios";
import type {BrightcoveApiConfig} from "@/app2/shared/data/repos/brightcove";
import dayjs from "dayjs";
import CustomDateTimePickerField from "@/app2/shared/components/FormComponents/CustomDateTimePickerField";
import {FileDropzone} from "@buildwitt/shapeshift-common/v1/fileDropzone";
import type {IDropzoneFile} from "@buildwitt/shapeshift-common/v1/fileDropzone/fileDropzoneFile";
import type {IFile} from "@buildwitt/shapeshift-types";
import {VideoUploadType} from "@/types/video";
import ConfirmationDialog from "@/components/common/dialogs/ConfirmationDialog";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {PublishedAudienceLockedAlert} from "@/pages/ManagerView/Pages/Annoucements/PublishedAudienceLockedAlert";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";

/** OpenAPI client types these as index signatures; API accepts string/null (see improve-api). */
type AnnouncementVideoUrlField = CreateAnnouncementBody["videoUrl"];
type AnnouncementVideoTypeField = CreateAnnouncementBody["videoType"];

const ANNOUNCEMENT_FILE_MAX_SIZE_MB = 50;
const ANNOUNCEMENT_FILE_MAX_SIZE_BYTES = ANNOUNCEMENT_FILE_MAX_SIZE_MB * 1024 * 1024;
const ANNOUNCEMENT_FILE_MAX_SIZE_HELPER_TEXT =
	"Click to select a file, or drag them in from your device's files. Max file size: 50MB.";

function fileIdsKeyFromFiles(list: (IFile | IDropzoneFile)[]): string {
	return list
		.map((f) => {
			if ("id" in f && typeof (f as IFile).id === "number" && (f as IFile).id > 0) {
				return `id:${(f as IFile).id}`;
			}
			const d = f as IDropzoneFile;
			if (d.file) return `new:${d.file.name}:${d.file.size}`;
			return "";
		})
		.filter(Boolean)
		.join(",");
}

function fileKey(file: IFile | IDropzoneFile): string {
	if ("id" in file && typeof (file as IFile).id === "number" && (file as IFile).id > 0) {
		return `id:${(file as IFile).id}`;
	}
	const dropzoneFile = file as IDropzoneFile;
	if (dropzoneFile.file instanceof File && dropzoneFile.file.size > 0) {
		return `new:${dropzoneFile.file.name}:${dropzoneFile.file.size}:${dropzoneFile.file.lastModified}`;
	}
	return "";
}

function fileId(file: IFile | IDropzoneFile): number | null {
	if ("id" in file && typeof (file as IFile).id === "number" && (file as IFile).id > 0) {
		return (file as IFile).id;
	}
	return null;
}

function mergeFileDropzoneChanges(
	previousFiles: (IFile | IDropzoneFile)[],
	incomingFiles: IDropzoneFile[]
): (IFile | IDropzoneFile)[] {
	const previousIds = previousFiles.map(fileId).filter((id): id is number => id != null);
	const incomingIds = new Set(incomingFiles.map(fileId).filter((id): id is number => id != null));
	const removedExistingFile = previousIds.some((id) => !incomingIds.has(id));

	if (removedExistingFile || incomingFiles.length < previousFiles.length) {
		return incomingFiles;
	}

	const incomingKeys = new Set(incomingFiles.map(fileKey).filter(Boolean));
	const missingPreviousNewFiles = previousFiles.filter((file) => {
		if (fileId(file) != null) return false;
		const key = fileKey(file);
		return key && !incomingKeys.has(key);
	});

	if (missingPreviousNewFiles.length === 0) {
		return incomingFiles;
	}

	const firstIncomingNewIndex = incomingFiles.findIndex((file) => fileId(file) == null);
	if (firstIncomingNewIndex === -1) {
		return [...incomingFiles, ...missingPreviousNewFiles];
	}

	return [
		...incomingFiles.slice(0, firstIncomingNewIndex),
		...missingPreviousNewFiles,
		...incomingFiles.slice(firstIncomingNewIndex)
	];
}

const useStyles = makeStyles()((theme) => ({
	root: {
		minHeight: "100vh",
		paddingBottom: theme.spacing(10)
	},
	adminAnnouncementPage: {},
	formFieldset: {
		borderRadius: "4px",
		"&.Mui-expanded": {margin: 0}
	},
	formFieldsetSummary: {
		padding: theme.spacing(0, 3),
		".MuiAccordionSummary-content": {
			display: "flex",
			flexDirection: "column",
			gap: theme.spacing(1)
		}
	},
	subtitle: {opacity: 0.7},
	attachedFilesHeader: {
		fontWeight: 600,
		color: theme.palette.text.primary
	},
	formFieldsetDetails: {
		display: "flex",
		flexDirection: "column",
		gap: theme.spacing(4),
		paddingLeft: theme.spacing(3),
		paddingRight: theme.spacing(3),
		paddingTop: theme.spacing(2),
		paddingBottom: theme.spacing(3)
	},
	formFieldsetNumber: {
		lineHeight: 1,
		display: "flex",
		flexDirection: "column",
		alignItems: "center",
		justifyContent: "center",
		height: "24px",
		marginRight: theme.spacing(2),
		color: theme.palette.primary.contrastText,
		borderRadius: "100%",
		aspectRatio: "1 / 1",
		backgroundColor: theme.palette.primary.main
	},
	actions: {
		position: "sticky",
		bottom: 0,
		backgroundColor: theme.palette.background.paper,
		borderTop: `1px solid ${theme.palette.grey[200]}`,
		padding: theme.spacing(2, 0),
		marginTop: theme.spacing(4),
		display: "flex",
		gap: theme.spacing(2),
		justifyContent: "flex-start",
		alignItems: "center",
		flexWrap: "wrap",
		zIndex: 100,
		boxShadow: "0 -2px 8px rgba(0,0,0,0.1)"
	},
	cancelBtn: {
		border: "none"
	},
	uploadColumn: {
		display: "inline-flex",
		flexDirection: "column",
		alignItems: "stretch",
		maxWidth: "100%",
		flexShrink: 0
	},
	uploadBtn: {
		height: "56px",
		color: theme.palette.primary.main,
		"&:hover": {backgroundColor: "transparent"},
		"&.Mui-disabled": {borderColor: theme.palette.divider, opacity: 0.38},
		flexShrink: 0,
		alignSelf: "flex-start"
	},
	videoUrlChip: {
		border: `1px solid ${theme.palette.primary.main}`,
		".MuiChip-deleteIcon": {"&, &:hover": {color: theme.palette.primary.main}}
	},
	uploadRow: {
		flexDirection: "column",
		alignItems: "stretch",
		width: "100%",
		rowGap: theme.spacing(2),
		[theme.breakpoints.up("sm")]: {
			flexDirection: "row",
			flexWrap: "wrap",
			alignItems: "flex-start"
		}
	},
	videoUploadField: {
		width: "100%",
		minWidth: 0,
		[theme.breakpoints.up("sm")]: {
			flex: "1 1 280px",
			minWidth: 280,
			maxWidth: "100%"
		}
	},
	uploadInput: {width: "100%",
		".MuiFormHelperText-root": {
			color: "#F9F9F9B2"
		}},
	uploadInputChip: {"input": {opacity: 0}}
}));

export const AdminAnnouncementPage: FC = () => {
	const {announcementId} = useParams();
	const id = announcementId ? parseInt(announcementId, 10) : undefined;
	const isEdit = id != null;
	const {data: existing, isLoading} = useGetAnnouncement(id, {enabled: isEdit});
	const queryClient = useQueryClient();
	const createMutation = useCreateAnnouncement();
	const updateMutation = useUpdateAnnouncement();
	const publishMutation = usePublishAnnouncement();
	const draftMutation = useDraftAnnouncement();
	const uploadFileMutation = useUploadLessonFile();
	const navigate = useNavigate();
	const {successSnackbar, errorSnackbar} = useCustomSnackbar();
	const apiConfig = useImproveAxios() as BrightcoveApiConfig;

	const {classes, cx} = useStyles();

	/** Set after deferred Brightcove upload (create flow); flushed on save via {@link completeBrightcoveIngest}. */
	const pendingBrightcoveCompleteRef = useRef<{videoId: string; videoUrl: string} | null>(null);
	/** True while file is uploaded but complete (ingest) not yet run — avoids blocking Publish as "still processing". */
	const [pendingBrightcoveFlush, setPendingBrightcoveFlush] = useState(false);

	const [title, setTitle] = useState("");
	const [subtitle, setSubtitle] = useState("");
	const [videoUrl, setVideoUrl] = useState("");
	const [videoType, setVideoType] = useState<"url" | "brightcove">("url");
	const [uploadState, setUploadState] = useState<VideoUploadType>("idle");
	const [videoId, setVideoId] = useState<string | null>(null);
	const [audienceType, setAudienceType] = useState<"all-employees" | "specific-employees">("specific-employees");
	const [selectedUserIds, setSelectedUserIds] = useState<number[]>([]);
	const [deliveryType, setDeliveryType] = useState<"publish-immediately" | "schedule-delivery">("schedule-delivery");
	const [scheduledDeliveryDate, setScheduledDeliveryDate] = useState<dayjs.Dayjs | null>(null);
	const [links, setLinks] = useState<string[]>([]);
	const [files, setFiles] = useState<(IFile | IDropzoneFile)[]>([]);
	const [fileDropzoneResetKey, setFileDropzoneResetKey] = useState(0);
	const [audienceError, setAudienceError] = useState(false);

	const brightcoveQueryVideoId =
		videoType === "brightcove"
			? (() => {
				const raw = videoId ?? videoUrl;
				return raw && raw.trim() !== "" ? raw : null;
			})()
			: null;

	const {data: brightcoveVideoQuery} = useBrightcoveQueryVideo(brightcoveQueryVideoId);
	const isBrightcovePlaybackReady = isBrightcoveQueryPlayable(brightcoveVideoQuery);

	const announcementStatus = existing ? getAnnouncementAdminDisplayStatus(existing) : null;
	const isPublished = !!existing?.publishedAt;
	const isScheduled = announcementStatus === "scheduled";
	const isInactive = announcementStatus === "inactive";
	const saveButtonLabel = isEdit && isPublished ? "Save" : "Save As Draft";
	const isSaving =
		createMutation.isPending ||
		updateMutation.isPending ||
		publishMutation.isPending ||
		draftMutation.isPending;
	/** Ingest has run and Brightcove query polls until the asset is playable. */
	const brightcoveStillProcessing =
		videoType === "brightcove" &&
		!!videoUrl &&
		!pendingBrightcoveFlush &&
		!isBrightcovePlaybackReady;

	const showBrightcoveProcessingNotice =
		videoType === "brightcove" &&
		!!brightcoveQueryVideoId &&
		!pendingBrightcoveFlush &&
		!isBrightcovePlaybackReady;

	const {t} = useSSTranslation();
	const [openExitPageModal, setOpenExitPageModal] = useState(false);
	// Bypasses the useBlocker check for intentional navigation (after save/delete)
	const forceExitPageRef = useRef(false);
	// Ref (not state) because initial values are only used for comparison, not rendering
	const initialDataRef = useRef({
		title: "",
		subtitle: "",
		videoUrl: "",
		deliveryType: "schedule-delivery" as "publish-immediately" | "schedule-delivery",
		scheduledDeliveryDate: null as dayjs.Dayjs | null,
		links: [] as string[],
		fileIdsKey: ""
	});

	useEffect(() => {
		if (!existing) return;
		forceExitPageRef.current = false;
		setTitle(existing.title ?? "");
		setSubtitle(existing.subtitle ?? "");
		const rawVideoUrl = existing.videoUrl ?? "";
		const apiVideoType = existing.videoType?.toLowerCase();
		const isBrightcoveVideo =
			apiVideoType === "brightcove" ||
			(rawVideoUrl.trim() !== "" && !/^https?:\/\//i.test(rawVideoUrl.trim()));
		setVideoUrl(rawVideoUrl);
		setVideoType(isBrightcoveVideo ? "brightcove" : "url");
		setVideoId(isBrightcoveVideo && rawVideoUrl ? rawVideoUrl : null);
		setLinks(Array.isArray(existing.links) ? (existing.links as string[]) : []);
		const loadedFiles: (IFile | IDropzoneFile)[] = Array.isArray(
			(existing as {files?: unknown}).files
		)
			? ((existing as unknown as {files: IFile[]}).files as (IFile | IDropzoneFile)[])
			: [];
		setFiles(loadedFiles);
		const serverDelivery = existing.deliveryType;
		const initDeliveryType =
			serverDelivery === "schedule-delivery" || serverDelivery === "publish-immediately"
				? serverDelivery
				: existing.scheduledDeliveryDate
					? "schedule-delivery"
					: "publish-immediately";
		const initScheduledDate = existing.scheduledDeliveryDate ? dayjs(existing.scheduledDeliveryDate) : null;
		setScheduledDeliveryDate(initScheduledDate);
		setDeliveryType(initDeliveryType);

		initialDataRef.current = {
			title: existing.title ?? "",
			subtitle: existing.subtitle ?? "",
			videoUrl: existing.videoUrl ?? "",
			deliveryType: initDeliveryType,
			scheduledDeliveryDate: initScheduledDate,
			links: Array.isArray(existing.links) ? (existing.links as string[]) : [],
			fileIdsKey: fileIdsKeyFromFiles(loadedFiles)
		};

		// Set audience type and selected users from server (create flow defaults to specific-employees)
		const existingAny = existing;
		if (existingAny.audienceType === "specific-employees") {
			setAudienceType("specific-employees");
			setSelectedUserIds(existingAny.selectedUserIds ?? []);
		} else {
			setAudienceType("all-employees");
			setSelectedUserIds([]);
		}
	}, [existing]);

	useEffect(() => {
		if (audienceType === "all-employees" || selectedUserIds.length > 0) {
			setAudienceError(false);
		}
	}, [audienceType, selectedUserIds]);

	const hasUnsavedChanges = useCallback(() => {
		const init = initialDataRef.current;
		return (
			title !== init.title ||
			subtitle !== init.subtitle ||
			videoUrl !== init.videoUrl ||
			deliveryType !== init.deliveryType ||
			scheduledDeliveryDate?.toISOString() !== init.scheduledDeliveryDate?.toISOString() ||
			JSON.stringify(links) !== JSON.stringify(init.links) ||
			fileIdsKeyFromFiles(files) !== init.fileIdsKey
		);
	}, [title, subtitle, videoUrl, deliveryType, scheduledDeliveryDate, links, files]);

	const blocker = useBlocker(() => hasUnsavedChanges() && !forceExitPageRef.current);

	useEffect(() => {
		if (blocker.state === "blocked") {
			setOpenExitPageModal(true);
		}
	}, [blocker]);

	// useBlocker doesn't cover browser refresh / tab close — handle via native prompt
	useEffect(() => {
		const handleBeforeUnload = (event: BeforeUnloadEvent) => {
			if (hasUnsavedChanges()) {
				event.preventDefault();
				event.returnValue = "";
				return true;
			}
			return false;
		};

		window.addEventListener("beforeunload", handleBeforeUnload);
		return () => window.removeEventListener("beforeunload", handleBeforeUnload);
	}, [hasUnsavedChanges]);

	const flushPendingBrightcoveComplete = useCallback(
		async (announcementId: number): Promise<boolean> => {
			const pending = pendingBrightcoveCompleteRef.current;
			if (!pending) return true;
			const res = await completeBrightcoveIngest(apiConfig, {
				entityType: "announcement",
				entityId: announcementId,
				videoId: pending.videoId,
				videoUrl: pending.videoUrl
			});
			if (!res.success) {
				errorSnackbar("error" in res ? res.error : "Failed to attach video");
				return false;
			}
			pendingBrightcoveCompleteRef.current = null;
			setPendingBrightcoveFlush(false);
			await queryClient.invalidateQueries({
				queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(announcementId)
			});
			return true;
		},
		[apiConfig, errorSnackbar, queryClient]
	);

	const buildBody = useCallback((): CreateAnnouncementBody &
	UpdateAnnouncementBody & {
		audienceUserIds?: number[];
	} => {
		const body: CreateAnnouncementBody &
		UpdateAnnouncementBody & {
			audienceUserIds?: number[];
		} = {
			title: title || undefined,
			subtitle: subtitle || undefined,
			deliveryType,
			scheduledDeliveryDate:
				deliveryType === "schedule-delivery" && scheduledDeliveryDate
					? scheduledDeliveryDate.toISOString()
					: undefined,
			...(audienceType === "specific-employees" && {audienceUserIds: selectedUserIds})
		};
		if (!isEdit) {
			(body as CreateAnnouncementBody).audienceType = audienceType as CreateAnnouncementBody["audienceType"];
		}
		// Same payload for create (POST) and update (PUT); previously only edit sent video fields.
		const trimmedVideo = videoUrl.trim();
		if (trimmedVideo) {
			body.videoUrl = trimmedVideo as unknown as AnnouncementVideoUrlField;
			body.videoType = (videoType === "brightcove" ? "brightcove" : "url") as unknown as AnnouncementVideoTypeField;
		} else {
			body.videoUrl = null as unknown as AnnouncementVideoUrlField;
			body.videoType = null as unknown as AnnouncementVideoTypeField;
		}
		return body;
	}, [
		title,
		subtitle,
		audienceType,
		selectedUserIds,
		deliveryType,
		scheduledDeliveryDate,
		isEdit,
		videoUrl,
		videoType
	]);

	const buildPersistBody = useCallback(async () => {
		const base = buildBody();
		const existingFileIds: number[] = [];
		const newFiles: File[] = [];
		for (const f of files) {
			if ("id" in f && typeof (f as IFile).id === "number" && (f as IFile).id > 0) {
				existingFileIds.push((f as IFile).id);
			} else if ("file" in f && (f as IDropzoneFile).file instanceof File && (f as IDropzoneFile).file.size > 0) {
				newFiles.push((f as IDropzoneFile).file);
			}
		}
		const uploadedFileIds: number[] = [];
		for (const file of newFiles) {
			const result = await uploadFileMutation.mutateAsync(file);
			if (result.files?.[0]?.id) uploadedFileIds.push(result.files[0].id);
		}
		const allFileIds = [...existingFileIds, ...uploadedFileIds];
		return {
			...base,
			links: [...links],
			existingFileIds: allFileIds
		} as CreateAnnouncementBody & UpdateAnnouncementBody & {audienceUserIds?: number[]};
	}, [buildBody, files, links, uploadFileMutation]);

	/** Draft save: title only. Publish path adds audience + schedule checks separately. */
	const validateForDraftPersist = useCallback((): boolean => {
		if (!title.trim()) {
			errorSnackbar("Title is required");
			return false;
		}
		return true;
	}, [title, errorSnackbar]);

	const validateScheduleForPublish = useCallback((): boolean => {
		if (deliveryType === "schedule-delivery" && !scheduledDeliveryDate) {
			errorSnackbar("Please select a delivery date and time");
			return false;
		}
		if (
			deliveryType === "schedule-delivery" &&
			scheduledDeliveryDate &&
			!scheduledDeliveryDate.isAfter(dayjs())
		) {
			errorSnackbar("Scheduled delivery must be in the future.");
			return false;
		}
		return true;
	}, [deliveryType, scheduledDeliveryDate, errorSnackbar]);

	const validateAudienceForPublish = useCallback((): boolean => {
		if (audienceType === "specific-employees" && selectedUserIds.length === 0) {
			setAudienceError(true);
			errorSnackbar("At least one audience member must be selected");
			return false;
		}
		return true;
	}, [audienceType, selectedUserIds, errorSnackbar]);

	const handleFilesChange = useCallback((newFiles: IDropzoneFile[]) => {
		const allowedFiles = newFiles.filter(
			(file) => !(file.file instanceof File && file.file.size > ANNOUNCEMENT_FILE_MAX_SIZE_BYTES)
		);

		if (allowedFiles.length !== newFiles.length) {
			errorSnackbar(
				`Some files are larger than ${ANNOUNCEMENT_FILE_MAX_SIZE_MB}MB and were not added. They will not be published with this announcement.`
			);
			setFileDropzoneResetKey((key) => key + 1);
		}

		setFiles((previousFiles) => mergeFileDropzoneChanges(previousFiles, allowedFiles));
	}, [errorSnackbar]);

	const handleSaveDraft = useCallback(async () => {
		if (!validateForDraftPersist()) return;
		const body = await buildPersistBody();
		try {
			if (isEdit && id != null) {
				await updateMutation.mutateAsync({id, body: body as UpdateAnnouncementBody});
				const ingestOk = await flushPendingBrightcoveComplete(id);
				if (!ingestOk) return;
				successSnackbar("Announcement saved successfully");
				await queryClient.refetchQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id)});
				await queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
			} else {
				const result = await createMutation.mutateAsync(body);
				const ingestOk = await flushPendingBrightcoveComplete(result.id);
				await queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
				forceExitPageRef.current = true;
				navigate(`../${result.id}/edit`, {replace: true});
				if (ingestOk) {
					successSnackbar("Announcement saved successfully");
				}
			}
		} catch {
			errorSnackbar("Failed to save announcement");
		}
	}, [
		validateForDraftPersist,
		buildPersistBody,
		isEdit,
		id,
		updateMutation,
		createMutation,
		queryClient,
		navigate,
		successSnackbar,
		errorSnackbar,
		flushPendingBrightcoveComplete
	]);

	const handlePublishToggle = useCallback(async () => {
		if (!isEdit || !isPublished) {
			if (!validateForDraftPersist()) return;
			if (!validateAudienceForPublish()) return;
			if (!validateScheduleForPublish()) return;
			if (brightcoveStillProcessing) {
				errorSnackbar("Wait for video processing to finish before publishing.");
				return;
			}
			const body = await buildPersistBody();
			try {
				let announcementId: number;
				if (!isEdit) {
					const result = await createMutation.mutateAsync(body);
					announcementId = result.id;
				} else {
					await updateMutation.mutateAsync({id: id!, body: body as UpdateAnnouncementBody});
					announcementId = id!;
				}
				const ingestOk = await flushPendingBrightcoveComplete(announcementId);
				if (!ingestOk) return;
				await publishMutation.mutateAsync(announcementId);
				successSnackbar("Announcement saved and published successfully");
				await queryClient.refetchQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(announcementId)});
				await queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
				if (!isEdit) {
					forceExitPageRef.current = true;
					navigate(`../${announcementId}/edit`, {replace: true});
				}
			} catch {
				errorSnackbar("Failed to publish announcement");
			}
			return;
		}

		try {
			await draftMutation.mutateAsync(id!);
			successSnackbar("Announcement unpublished successfully");
			await queryClient.refetchQueries({queryKey: ANNOUNCEMENTS_QUERY_KEYS.adminDetail(id!)});
			await queryClient.invalidateQueries({queryKey: [ANNOUNCEMENTS_QUERY_KEYS.ADMIN_LIST]});
		} catch {
			errorSnackbar("Failed to unpublish announcement");
		}
	}, [
		isEdit,
		isPublished,
		id,
		validateForDraftPersist,
		validateAudienceForPublish,
		validateScheduleForPublish,
		buildPersistBody,
		brightcoveStillProcessing,
		createMutation,
		updateMutation,
		publishMutation,
		draftMutation,
		queryClient,
		navigate,
		successSnackbar,
		errorSnackbar,
		flushPendingBrightcoveComplete
	]);

	const handleExit = () => {
		blocker.proceed?.();
	};

	const handleKeepEditing = () => {
		blocker.reset?.();
		setOpenExitPageModal(false);
	};

	if (isEdit && isLoading) {
		return <Box sx={{p: 2}}>Loading…</Box>;
	}

	return (
		<Box className={cx(classes.root, classes.adminAnnouncementPage)}>
			<AdminHeader
				isScheduled={isScheduled}
				isInactive={isInactive}
				title={isEdit ? existing?.title ?? "Announcement" : "Create New"}
				subtitle={isEdit ? "" : "Announcement"}
				rootSlug="/admin/announcements"
				rootBreadcrumb={
					<>
						<PlayCircleFilledWhiteOutlinedIcon />
						<Link to="/admin/announcements">
							<Typography variant="body1">Announcements</Typography>
						</Link>
					</>
				}
				editMode={isEdit ? "edit" : "create"}
				entity={
					existing
						? ({
							id: existing.id,
							title: existing.title ?? "",
							publishedAt: existing.publishedAt ?? undefined,
							createdAt: existing.publishedAt ?? new Date().toISOString(),
							updatedAt: existing.publishedAt ?? new Date().toISOString()
						} as import("@buildwitt/shapeshift-types").IEntity & {title?: string})
						: undefined
				}
				type={"announcement" as any}
			/>
			<Box sx={{display: "grid", gridTemplateColumns: "repeat(12, 1fr)", gap: 2}}>
				<Box sx={{gridColumn: "span 12", display: "flex", flexDirection: "column", gap: 3}}>
					<Accordion defaultExpanded className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>1</span>
								<Typography variant="h6" component="p">
									Announcement Information
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>
								Add the basic details for your announcement.
							</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<TextField
								label="Announcement title"
								required
								value={title}
								onChange={(e) => setTitle(e.target.value)}
								fullWidth
								inputProps={{maxLength: 255}}
							/>
							<TextField
								label="Description"
								multiline
								rows={5}
								value={subtitle}
								onChange={(e) => setSubtitle(e.target.value)}
								fullWidth
							/>
						</AccordionDetails>
					</Accordion>
					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>2</span>
								<Typography variant="h6" component="p">
									Announcement Media
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>Upload your announcement video.</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<Stack className={classes.uploadRow}>
								<Box className={classes.videoUploadField}>
									<AdminInputText
										fullWidth
										className={cx(
											classes.uploadInput,
											videoType === "brightcove" &&
												!!videoUrl &&
												classes.uploadInputChip
										)}
										label="Video"
										helperText={"Upload a supported video file or paste a YouTube link (copy from the browser address bar). Max file size: 10GB. Saving the announcement attaches the video to the draft."}
										value={videoUrl}
										onChange={(e) => {
											setVideoUrl(e.target.value);
											setVideoType("url");
										}}
										InputProps={{
											readOnly:
											(videoType === "brightcove" && !!videoUrl) ||
											uploadState === "progress",
											startAdornment:
											videoType === "brightcove" && videoUrl ? (
												<Chip
													className={classes.videoUrlChip}
													label={videoUrl}
													onDelete={() => {
														setVideoId(null);
														setVideoType("url");
														setVideoUrl("");
														pendingBrightcoveCompleteRef.current = null;
														setPendingBrightcoveFlush(false);
													}}
												/>
											) : null
										}}
									/>
								</Box>
								<UploadBrightcoveVideo
									entityType="announcement"
									uploadState={uploadState}
									setUploadState={setUploadState}
									handleSuccess={(uploadVideoId, pending) => {
										setVideoId(uploadVideoId);
										setVideoType("brightcove");
										setVideoUrl(uploadVideoId);
										if (pending?.pendingBrightcoveComplete) {
											pendingBrightcoveCompleteRef.current = {
												videoId: uploadVideoId,
												videoUrl: pending.videoUrl
											};
											setPendingBrightcoveFlush(true);
										} else {
											pendingBrightcoveCompleteRef.current = null;
											setPendingBrightcoveFlush(false);
										}
									}}
									lessonId={id ?? 0}
								>
									{({onClick, progress}) => (
											<Box className={classes.uploadColumn}>
												<Button
													variant="outlined"
													className={classes.uploadBtn}
													onClick={onClick}
													startIcon={<UploadFileOutlinedIcon />}
													disabled={
														(videoType !== "brightcove" &&
															videoUrl &&
															uploadState === "idle") ||
														uploadState === "progress"
													}
												>
													{uploadState === "progress"
														? `Uploading… ${Math.ceil(progress/100*100)}%`
														: "Upload file"}
												</Button>
											</Box>
										)
									}
								</UploadBrightcoveVideo>
							</Stack>
							{showBrightcoveProcessingNotice && (
								<Typography>
									The video is still processing. Please wait until processing has
									completed to publish the announcement.
								</Typography>
							)}
						</AccordionDetails>
					</Accordion>
					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>3</span>
								<Typography variant="h6" component="p">
									Announcement Audience
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>
								Choose who receives this announcement.
							</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							{existing?.delivered && <PublishedAudienceLockedAlert />}
							<FormControl component="fieldset" disabled={!!existing?.delivered}>
								<RadioGroup
									row
									value={audienceType}
									onChange={(e) =>
										setAudienceType(e.target.value as "all-employees" | "specific-employees")
									}
									aria-labelledby="audience-radio"
									name="audience-radio"
								>
									<FormControlLabel
										value="all-employees"
										control={<Radio />}
										label="All Employees"
									/>
									<FormControlLabel
										value="specific-employees"
										control={<Radio />}
										label="Selected Employees"
									/>
								</RadioGroup>
							</FormControl>
							{audienceType === "specific-employees" && (
								<SpecificAudienceSelector
									selectedUserIds={selectedUserIds}
									onChange={setSelectedUserIds}
									disabled={!!existing?.delivered}
								/>
							)}
							{audienceError && audienceType === "specific-employees" && (
								<FormHelperText error>
									At least one audience member must be selected
								</FormHelperText>
							)}
						</AccordionDetails>
					</Accordion>
					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>4</span>
								<Typography variant="h6" component="p">
									Announcement Schedule
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>
								Choose the exact date and time when this announcement becomes visible to learners.
							</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<FormControl>
								<RadioGroup
									row
									value={deliveryType}
									onChange={(e) =>
										setDeliveryType(
											e.target.value as
												| "publish-immediately"
												| "schedule-delivery"
										)
									}
									aria-labelledby="delivery-radio"
									name="delivery-radio"
								>
									<FormControlLabel
										value="publish-immediately"
										control={<Radio />}
										label="Publish Immediately"
									/>
									<FormControlLabel
										value="schedule-delivery"
										control={<Radio />}
										label="Schedule Delivery"
									/>
								</RadioGroup>
							</FormControl>
							{deliveryType === "schedule-delivery" && (
								<CustomDateTimePickerField
									label="Delivery date & time"
									value={scheduledDeliveryDate}
									onChange={setScheduledDeliveryDate}
									minDate={dayjs().startOf("day")}
									dataTestId="announcement-scheduled-delivery-datetime"
									aria-label="Scheduled delivery date and time"
								/>
							)}
						</AccordionDetails>
					</Accordion>
					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>5</span>
								<Typography variant="h6" component="p">
									Include Links (Optional)
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>
								Include links to support learning.
							</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<AttachLinks links={links} onChange={setLinks} />
						</AccordionDetails>
					</Accordion>
					<Accordion className={classes.formFieldset}>
						<AccordionSummary
							className={classes.formFieldsetSummary}
							expandIcon={<ExpandMoreIcon />}
						>
							<Stack direction="row" alignItems="center">
								<span className={classes.formFieldsetNumber}>6</span>
								<Typography variant="h6" component="p">
									Include Files (Optional)
								</Typography>
							</Stack>
							<Typography className={classes.subtitle}>
								Include files to support learning.
							</Typography>
						</AccordionSummary>
						<AccordionDetails className={classes.formFieldsetDetails}>
							<Typography className={classes.attachedFilesHeader} variant="subtitle1">
								Attached Files
							</Typography>
							<FileDropzone
								key={fileDropzoneResetKey}
								value={files}
								onChange={handleFilesChange}
								placeholder={
									<Typography variant="body1">
										{ANNOUNCEMENT_FILE_MAX_SIZE_HELPER_TEXT}
									</Typography>
								}
							/>
						</AccordionDetails>
					</Accordion>
					<Box className={classes.actions}>
						{isPublished ? (
							<>
								<Button
									variant="contained"
									color="primary"
									onClick={() => void handleSaveDraft()}
									disabled={isSaving || !title.trim()}
								>
									{createMutation.isPending || updateMutation.isPending ? (
										<CircularProgress size={20} />
									) : (
										saveButtonLabel
									)}
								</Button>
								<Button
									variant="outlined"
									color="error"
									onClick={() => void handlePublishToggle()}
									disabled={isSaving}
								>
									{publishMutation.isPending || draftMutation.isPending ? (
										<CircularProgress size={20} />
									) : (
										"Unpublish"
									)}
								</Button>
							</>
						) : (
							<>
								<Button
									variant="contained"
									color="primary"
									onClick={() => void handlePublishToggle()}
									disabled={isSaving || brightcoveStillProcessing}
								>
									{publishMutation.isPending || draftMutation.isPending ? (
										<CircularProgress size={20} />
									) : (
										"Publish"
									)}
								</Button>
								<Button
									variant="outlined"
									color="primary"
									onClick={() => void handleSaveDraft()}
									disabled={isSaving || !title.trim()}
								>
									{createMutation.isPending || updateMutation.isPending ? (
										<CircularProgress size={20} />
									) : (
										saveButtonLabel
									)}
								</Button>
							</>
						)}
						<Button
							variant="text"
							className={classes.cancelBtn}
							onClick={() => {
								forceExitPageRef.current = true;
								navigate("/admin/announcements");
							}}
							disabled={isSaving}
							sx={{marginLeft: "auto"}}
						>
							Cancel
						</Button>
					</Box>
				</Box>
			</Box>
			{openExitPageModal && (
				<ConfirmationDialog
					title={t("common_unsaved_data_title", "Unsaved data will be lost")}
					content={t("common_unsaved_changes_message", "You'll lose any unsaved work if you exit now. Do you want to proceed?")}
					testIdPrefix="unsavedChanges"
					open={openExitPageModal}
					hideCloseIcon
					cancelButtonTitle={t("common_exit", "Exit")}
					confirmButtonTitle={t("common_keep_editing", "Keep Editing")}
					onCancel={handleExit}
					onConfirm={handleKeepEditing}
					preventBackDropClick
				/>
			)}
		</Box>
	);
};
